import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { Mail, UserCircle2, CheckCircle2, Lock, Eye, EyeOff, Sparkles, KeyRound } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input, Label, FieldError, FieldHint } from '@/components/ui/Input';
import { Card, CardDescription } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD = 6;
/**
 * Modal d'auth principale. Trois modes : signin (email+password), signup,
 * reset (password oublié). Plus deux fallbacks : magic link et invité.
 */
export function SignInModal({ open, onOpenChange }) {
    const { signInWithPassword, signUpWithPassword, resetPasswordForEmail, signInWithMagicLink, signInAnonymously, } = useAuth();
    const [mode, setMode] = useState('signin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [emailError, setEmailError] = useState(null);
    const [passwordError, setPasswordError] = useState(null);
    const [generalError, setGeneralError] = useState(null);
    const [pending, setPending] = useState(false);
    const [pendingMagic, setPendingMagic] = useState(false);
    const [pendingAnon, setPendingAnon] = useState(false);
    const [confirmation, setConfirmation] = useState(null);
    function reset() {
        setMode('signin');
        setEmail('');
        setPassword('');
        setPasswordConfirm('');
        setShowPassword(false);
        setEmailError(null);
        setPasswordError(null);
        setGeneralError(null);
        setPending(false);
        setPendingMagic(false);
        setPendingAnon(false);
        setConfirmation(null);
    }
    function close() {
        onOpenChange(false);
        setTimeout(reset, 300);
    }
    // ── Validation locale ──────────────────────────────────────────────────
    function validate() {
        setEmailError(null);
        setPasswordError(null);
        setGeneralError(null);
        if (!EMAIL_REGEX.test(email.trim())) {
            setEmailError("Format d'email invalide.");
            return false;
        }
        if (mode === 'reset')
            return true;
        if (password.length < MIN_PASSWORD) {
            setPasswordError(`Le mot de passe doit faire au moins ${MIN_PASSWORD} caractères.`);
            return false;
        }
        if (mode === 'signup' && password !== passwordConfirm) {
            setPasswordError('Les mots de passe ne correspondent pas.');
            return false;
        }
        return true;
    }
    // ── Submit principal selon le mode ─────────────────────────────────────
    async function onSubmit(e) {
        e.preventDefault();
        if (!validate())
            return;
        setPending(true);
        const trimmed = email.trim();
        let result;
        if (mode === 'signin')
            result = await signInWithPassword(trimmed, password);
        else if (mode === 'signup')
            result = await signUpWithPassword(trimmed, password);
        else
            result = await resetPasswordForEmail(trimmed);
        setPending(false);
        if (!result.ok) {
            setGeneralError(humanizeAuthError(result.error, mode));
            return;
        }
        if (result.needsConfirm) {
            setConfirmation({ kind: mode, email: trimmed });
            return;
        }
        // Succès direct (signin sans confirm) → AuthEventListener affichera le toast
        close();
    }
    async function onMagicLink() {
        if (!EMAIL_REGEX.test(email.trim())) {
            setEmailError("Saisis un email valide pour recevoir un lien magique.");
            return;
        }
        setPendingMagic(true);
        const result = await signInWithMagicLink(email.trim());
        setPendingMagic(false);
        if (!result.ok) {
            setGeneralError(result.error ?? "Échec de l'envoi.");
            return;
        }
        setConfirmation({ kind: 'magic', email: email.trim() });
    }
    async function onAnon() {
        setPendingAnon(true);
        try {
            await signInAnonymously();
            close();
        }
        catch {
            setGeneralError("Impossible de créer une session invitée.");
        }
        finally {
            setPendingAnon(false);
        }
    }
    // ── Rendu ──────────────────────────────────────────────────────────────
    if (confirmation) {
        return (_jsx(Modal, { open: open, onOpenChange: (o) => { if (!o)
                close(); }, title: "V\u00E9rifie ta bo\u00EEte mail", size: "md", children: _jsx(ConfirmationView, { kind: confirmation.kind, email: confirmation.email, onClose: close }) }));
    }
    const titles = {
        signin: 'Connexion',
        signup: 'Créer un compte',
        reset: 'Mot de passe oublié',
    };
    const descriptions = {
        signin: 'Connecte-toi avec ton email et mot de passe.',
        signup: 'Crée ton compte permanent. Tu pourras lier ton historique invité plus tard.',
        reset: 'On t\'envoie un lien pour réinitialiser ton mot de passe.',
    };
    return (_jsx(Modal, { open: open, onOpenChange: (o) => { if (!o)
            close(); }, title: titles[mode], description: descriptions[mode], size: "md", footer: null, children: _jsxs("div", { className: "space-y-5", children: [_jsxs("form", { onSubmit: (e) => void onSubmit(e), className: "space-y-3", children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "auth-email", required: true, children: "Email" }), _jsx(Input, { id: "auth-email", type: "email", autoComplete: "email", value: email, onChange: (e) => { setEmail(e.target.value); setEmailError(null); setGeneralError(null); }, placeholder: "vous@exemple.fr", leftIcon: _jsx(Mail, { className: "h-4 w-4" }), invalid: !!emailError }), emailError && _jsx(FieldError, { children: emailError })] }), mode !== 'reset' && (_jsxs("div", { children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx(Label, { htmlFor: "auth-password", required: true, children: "Mot de passe" }), mode === 'signin' && (_jsx("button", { type: "button", onClick: () => { setMode('reset'); setPasswordError(null); setGeneralError(null); }, className: "text-xs font-semibold text-electric-300 hover:underline", children: "Oubli\u00E9 ?" }))] }), _jsx(Input, { id: "auth-password", type: showPassword ? 'text' : 'password', autoComplete: mode === 'signup' ? 'new-password' : 'current-password', value: password, onChange: (e) => { setPassword(e.target.value); setPasswordError(null); setGeneralError(null); }, placeholder: mode === 'signup' ? `${MIN_PASSWORD} caractères minimum` : 'Ton mot de passe', leftIcon: _jsx(Lock, { className: "h-4 w-4" }), rightIcon: _jsx("button", { type: "button", "aria-label": showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe', onClick: () => setShowPassword((s) => !s), className: "text-ink-400 hover:text-ink-200", children: showPassword ? _jsx(EyeOff, { className: "h-4 w-4" }) : _jsx(Eye, { className: "h-4 w-4" }) }), invalid: !!passwordError }), mode === 'signup' && !passwordError && (_jsx(FieldHint, { children: "Choisis quelque chose dont tu te souviendras." }))] })), mode === 'signup' && (_jsxs("div", { children: [_jsx(Label, { htmlFor: "auth-password-confirm", required: true, children: "Confirmer le mot de passe" }), _jsx(Input, { id: "auth-password-confirm", type: showPassword ? 'text' : 'password', autoComplete: "new-password", value: passwordConfirm, onChange: (e) => { setPasswordConfirm(e.target.value); setPasswordError(null); }, leftIcon: _jsx(Lock, { className: "h-4 w-4" }), invalid: !!passwordError }), passwordError && _jsx(FieldError, { children: passwordError })] })), mode === 'signin' && passwordError && _jsx(FieldError, { children: passwordError }), generalError && (_jsx(Card, { variant: "ghost", padding: "sm", className: "border border-rose-500/40 bg-rose-500/5", children: _jsx("p", { className: "text-sm text-rose-300", children: generalError }) })), _jsx(Button, { type: "submit", variant: "primary", size: "lg", fullWidth: true, isLoading: pending, leftIcon: mode === 'signin' ? _jsx(Lock, { className: "h-4 w-4" }) :
                                mode === 'signup' ? _jsx(UserCircle2, { className: "h-4 w-4" }) :
                                    _jsx(KeyRound, { className: "h-4 w-4" }), children: mode === 'signin' ? 'Se connecter' : mode === 'signup' ? 'Créer mon compte' : 'Envoyer le lien' })] }), _jsx(ModeSwitcher, { mode: mode, onChange: (m) => { setMode(m); setGeneralError(null); setPasswordError(null); } }), mode !== 'reset' && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsxs("div", { className: "space-y-2", children: [_jsx(Button, { type: "button", variant: "secondary", size: "md", fullWidth: true, isLoading: pendingMagic, leftIcon: _jsx(Sparkles, { className: "h-4 w-4" }), onClick: () => void onMagicLink(), children: "M'envoyer un lien magique" }), _jsx(Button, { type: "button", variant: "ghost", size: "md", fullWidth: true, isLoading: pendingAnon, leftIcon: _jsx(UserCircle2, { className: "h-4 w-4" }), onClick: () => void onAnon(), children: "Continuer en invit\u00E9" })] })] })), mode === 'signup' && (_jsx(Card, { variant: "ghost", padding: "sm", className: "border border-ink-800", children: _jsxs(CardDescription, { className: "text-xs", children: [_jsx("strong", { className: "text-ink-200", children: "Confirmation par email." }), " Si le projet Supabase a \"Confirm email\" activ\u00E9, tu devras cliquer le lien envoy\u00E9 pour valider ton compte avant de pouvoir te connecter."] }) }))] }) }));
}
// ────────────────────────────────────────────────────────────────────────────
// Sous-composants
// ────────────────────────────────────────────────────────────────────────────
function ModeSwitcher({ mode, onChange }) {
    if (mode === 'reset') {
        return (_jsx("p", { className: "text-center text-sm text-ink-400", children: _jsx("button", { type: "button", onClick: () => onChange('signin'), className: modeBtnClass, children: "\u2190 Retour \u00E0 la connexion" }) }));
    }
    return (_jsxs("p", { className: "text-center text-sm text-ink-400", children: [mode === 'signin' ? 'Pas de compte ?' : 'Déjà un compte ?', ' ', _jsx("button", { type: "button", onClick: () => onChange(mode === 'signin' ? 'signup' : 'signin'), className: modeBtnClass, children: mode === 'signin' ? 'Créer un compte' : 'Se connecter' })] }));
}
const modeBtnClass = cn('font-semibold text-electric-300 hover:underline focus-visible:outline-2 focus-visible:outline-electric-400 focus-visible:rounded');
function Divider() {
    return (_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("span", { className: "h-px flex-1 bg-ink-800" }), _jsx("span", { className: "text-xs uppercase tracking-wider text-ink-500", children: "ou" }), _jsx("span", { className: "h-px flex-1 bg-ink-800" })] }));
}
function ConfirmationView({ kind, email, onClose, }) {
    const messages = {
        signin: { title: 'Lien envoyé', desc: 'Vérifie ta boîte mail pour confirmer la connexion.' },
        signup: { title: 'Compte créé — confirme-le', desc: `Un lien de confirmation a été envoyé à ${email}. Clique dessus pour activer ton compte.` },
        reset: { title: 'Lien de réinitialisation envoyé', desc: `Vérifie ${email} et clique sur le lien pour choisir un nouveau mot de passe.` },
        magic: { title: 'Lien magique envoyé', desc: `Clique le lien envoyé à ${email} pour te connecter — pas de mot de passe à retenir.` },
    };
    const m = messages[kind];
    return (_jsxs("div", { className: "space-y-4 text-center", children: [_jsx(CheckCircle2, { className: "mx-auto h-12 w-12 text-emerald-400 animate-pop-in" }), _jsx("h3", { className: "font-display text-xl uppercase tracking-tight text-ink-50", children: m.title }), _jsx("p", { className: "text-sm text-ink-300", children: m.desc }), _jsx("p", { className: "text-xs text-ink-500", children: "Pas re\u00E7u ? Regarde dans les spams, ou r\u00E9essaie dans 1 minute." }), _jsx(Button, { variant: "primary", fullWidth: true, onClick: onClose, children: "D'accord" })] }));
}
// ────────────────────────────────────────────────────────────────────────────
// Mapping erreurs Supabase → français
// ────────────────────────────────────────────────────────────────────────────
function humanizeAuthError(message, mode) {
    const m = (message ?? '').toLowerCase();
    if (m.includes('invalid login') || m.includes('invalid_grant') || m.includes('invalid credentials')) {
        return 'Email ou mot de passe incorrect.';
    }
    if (m.includes('email not confirmed')) {
        return 'Ton email n\'est pas encore confirmé. Vérifie ta boîte mail.';
    }
    if (m.includes('user already registered') || m.includes('already exists')) {
        return mode === 'signup' ? 'Un compte existe déjà avec cet email — connecte-toi à la place.' : message ?? 'Conflit.';
    }
    if (m.includes('password should be') || m.includes('password is too')) {
        return `Mot de passe trop faible. ${MIN_PASSWORD} caractères minimum.`;
    }
    if (m.includes('rate limit') || m.includes('too many requests')) {
        return 'Trop de tentatives. Réessaie dans une minute.';
    }
    if (m.includes('network')) {
        return 'Problème de connexion. Réessaie.';
    }
    return message ?? 'Erreur inattendue.';
}
